'use strict'

import React from 'react';
import { useEffect, useState, useRef, useMemo } from 'react';

import { Button, Form, Modal, Row, Col, Table, OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import useRequest from '../helper/useRequest';
import Loader from '../helper/Loader';
import Alert from '../helper/Alert';

import useConfirmation from '../helper/Confirmation';
import Select from 'react-select';

import TableHeader from '../helper/TableHeader';
import { customFilterFunction } from '../helper/CustomFilter';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table';

import { formatDate } from '../helper/DateTimeFormater';

import { code } from '../helper/Schema';

const columnHelper = createColumnHelper();

function ModalEditCoupon({ modal, setModal, coupon, updateNote, message }) 
{
    const formRef = useRef(null);

    const schema = {
        Code: code,
    };

    const closeModal = () => {
        setModal(false);
    }

    const onSubmit = (values) => {
        updateNote(values);
    }

    useEffect(() => {
        if (!formRef.current) return;
        formRef.current.setFieldValue("CouponID", coupon.CouponID);
        formRef.current.setFieldValue("Code", coupon.Code);
        formRef.current.setFieldValue("DiscountPercentage", coupon.DiscountPercentage);
        formRef.current.setFieldValue("MaxBookings", coupon.MaxBookings);
        formRef.current.setFieldValue("Notes", coupon.Notes);
        formRef.current.setFieldValue("Activated", coupon.Activated);
        formRef.current.setFieldValue("Expired", coupon.Expired);

    }, [coupon]);

    return (
        <Modal size="lg" show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>Gutschein bearbeiten</Modal.Title>
            </Modal.Header>
                <Formik
                    innerRef={(f) => formRef.current = f}
                    validationSchema={
                        yup.object().shape(schema)
                    }
                    onSubmit={(values) => {
                        onSubmit(values);
                    }}
                    initialValues={{
                        CouponID: 0,
                        Code: "",
                        DiscountPercentage: 0,
                        MaxBookings: 1,
                        Notes: "",
                        Activated: "",
                        Expired: "",
                    }}
                >
                    {
                        ({
                            handleSubmit,
                            handleChange,
                            handleBlur,
                            values,
                            touched,
                            isValid,
                            errors,
                        }) => {
                            return (
                                <>
                                    <Modal.Body>
                                        {message && <Alert type="error" message={message} hideIcon={true} />}
                                        <Form noValidate onSubmit={handleSubmit}>
                                            <Form.Group as={Row} className="mb-3" controlId="Code">
                                                <Form.Label column sm={6}>Code</Form.Label>
                                                <Col>
                                                    <Form.Control type="text" isInvalid={errors.Code && touched.Code} value={values.Code} onChange={handleChange} onBlur={handleBlur} />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.Code}
                                                    </Form.Control.Feedback>
                                                </Col>
                                            </Form.Group>
                                            <Form.Group as={Row} className="mb-3" controlId="DiscountPercentage">
                                                <Form.Label column sm={6}>Rabatt in %</Form.Label>
                                                <Col>
                                                    <Form.Control type="number" value={values.DiscountPercentage} min={0} step={1} onChange={handleChange} />
                                                </Col>
                                            </Form.Group>
                                            <Form.Group as={Row} className="mb-3" controlId="MaxBookings">
                                                <Form.Label column sm={6}>Maximale Nutzung</Form.Label>
                                                <Col>
                                                    <Form.Control type="number" value={values.MaxBookings} min={0} step={1} onChange={handleChange} />
                                                </Col>
                                            </Form.Group>
                                            <Form.Group as={Row} className="mb-3" controlId="Notes">
                                                <Form.Label column sm={6}>Notizen</Form.Label>
                                                <Col>
                                                    <Form.Control type="text" value={values.Notes} onChange={handleChange} />
                                                </Col>
                                            </Form.Group>
                                            <Form.Group as={Row} className="mb-3" controlId="Activated">
                                                <Form.Label column sm={6}>Gültig ab dem:</Form.Label>
                                                <Col>
                                                    <Form.Control type="date" value={values.Activated} onChange={handleChange} />
                                                </Col>
                                            </Form.Group>
                                            <Form.Group as={Row} className="mb-3" controlId="Expired">
                                                <Form.Label column sm={6}>Gültig bis zum:</Form.Label>
                                                <Col>
                                                    <Form.Control type="date" value={values.Expired} onChange={handleChange} />
                                                </Col>
                                            </Form.Group>
                                        </Form>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button type="button" variant="secondary" onClick={closeModal}>
                                            Abbrechen
                                        </Button>
                                        <Button type="submit" variant="primary" onClick={handleSubmit}>
                                            Speichern
                                        </Button>
                                    </Modal.Footer>
                                </>
                            );
                        }
                    }
                </Formik>
        </Modal>
    );
}

function ModalAsignCoupon({ modal, setModal, coupon, assignCoupon, organisations }) {
    const [message, setMessage] = useState("");
    const [organisation, setOrganisation] = useState(0);

    const closeModal = () => {
        setModal(false);
    }
    const onSubmit = (coupon) => {
        if (organisation == 0) {
            setMessage("Bitte wählen Sie eine Organisation aus.");
        }
        else {
            setMessage("");
            assignCoupon(coupon, organisation.value);
        }
    }

    const options = useMemo(() => {
        return organisations.map((orga) => { return { value: orga.OrganisationID, label: orga.OrganisationName } })
    }, [organisations]);

    return (
        <Modal size="lg" show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>Gutschein zuordnen</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {message && <Alert type="error" message={message} />}
                <Form.Label>Organisation, welcher der Gutschein zugeordnet werden soll:</Form.Label>
                <Select id="organisation" options={options} value={organisation} onChange={(option) => setOrganisation(option)} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    Abbrechen
                </Button>
                <Button variant="primary" onClick={() => onSubmit(coupon)}>
                    Speichern
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function ModalCreateCoupon({ modal, setModal, createCoupon }) {
    const [message, setMessage] = useState("");

    const formRef = useRef(null);

    const schema = {
        Code: code,
    };

    const closeModal = () => {
        setMessage("");
        setModal(false);
    }

    const onSubmit = (values) => {
        createCoupon(values.Code, values.MaxBookings, values.DiscountPercentage, values.Notes, values.Activated, values.Expired);
    }

    return (
        <Modal size="lg" show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>Gutschein erstellen</Modal.Title>
            </Modal.Header>
                <Formik
                    innerRef={(f) => formRef.current = f}
                    validationSchema={
                        yup.object().shape(schema)
                    }
                    onSubmit={(values) => {
                        onSubmit(values);
                    }}
                    initialValues={{
                        Code: "",
                        DiscountPercentage: 0,
                        MaxBookings: 1,
                        Notes: "",
                        Activated: "",
                        Expired: "",
                    }}
                >
                    {
                        ({
                            handleSubmit,
                            handleChange,
                            handleBlur,
                            values,
                            touched,
                            isValid,
                            errors,
                        }) => {
                            return (
                                <>
                                    <Modal.Body>
                                        {message && <Alert type="error" message={message} />}
                                    <Form noValidate onSubmit={handleSubmit}>
                                        <Form.Group as={Row} className="mb-3" controlId="Code">
                                            <Form.Label column sm={6}>Code</Form.Label>
                                            <Col>
                                                    <Form.Control type="text" isInvalid={errors.Code && touched.Code} value={values.Code} onChange={handleChange} onBlur={handleBlur} />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.Code}
                                                    </Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="DiscountPercentage">
                                            <Form.Label column sm={6}>Rabatt in %</Form.Label>
                                            <Col>
                                                <Form.Control type="number" value={values.DiscountPercentage} min={0} step={1} onChange={handleChange} />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="MaxBookings">
                                            <Form.Label column sm={6}>Maximale Nutzung</Form.Label>
                                            <Col>
                                                    <Form.Control type="number" value={values.MaxBookings} min={0} step={1} onChange={handleChange} />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="Notes">
                                            <Form.Label column sm={6}>Notizen</Form.Label>
                                            <Col>
                                                <Form.Control type="text" value={values.Notes} onChange={handleChange} />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="Activated">
                                            <Form.Label column sm={6}>Gültig ab dem:</Form.Label>
                                            <Col>
                                                <Form.Control type="date" value={values.Activated} onChange={handleChange} />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="Expired">
                                            <Form.Label column sm={6}>Gültig bis zum:</Form.Label>
                                            <Col>
                                                <Form.Control type="date" value={values.Expired} onChange={handleChange} />
                                            </Col>
                                        </Form.Group>
                                    </Form>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button type="button" variant="secondary" onClick={closeModal}>
                                            Abbrechen
                                        </Button>
                                        <Button type="submit" variant="primary" onClick={handleSubmit}>
                                            Erstellen
                                        </Button>
                                    </Modal.Footer>
                                </>
                            );
                        }
                    }
                </Formik>
        </Modal>
    );
}

function ModalActivateCoupon({ modal, setModal, coupon, activateCoupon }) {
    const [message, setMessage] = useState("");

    const formRef = useRef(null);

    const closeModal = () => {
        setMessage("");
        setModal(false);
    }

    const onSubmit = (values) => {
        if (values.activationDate.length == 0 || values.expirationDate.length == 0) {
            setMessage("Es muss ein Anfangs- und End-Datum angegeben werden")
            return;
        }

        activateCoupon(coupon, values.activationDate, values.expirationDate);
        closeModal();
    }

    return (
        <Modal size="lg" show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>Gutschein aktivieren</Modal.Title>
            </Modal.Header>
            <Formik
                innerRef={
                    (f) => formRef.current = f
                }
                onSubmit={
                    (values) => { onSubmit(values); }
                }
                initialValues={
                    {
                        activationDate: "",
                        expirationDate: ""
                    }
                }
            >
                {
                    ({
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        values,
                        touched,
                        isValid,
                        errors,
                    }) => {
                        return (
                            <>
                                <Modal.Body>
                                    {message && <Alert type="error" message={message} />}
                                    <Form>
                                        <Form.Group as={Row} className="mb-3" controlId="activationDate">
                                            <Form.Label column sm={6}>Aktiv ab dem:</Form.Label>
                                            <Col>
                                                <Form.Control type="date" value={values.activationDate} onChange={handleChange} onBlur={handleBlur}/>
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="expirationDate">
                                            <Form.Label column sm={6}>Aktiv bis zum:</Form.Label>
                                            <Col>
                                                <Form.Control type="date" value={values.expirationDate} onChange={handleChange} onBlur={handleBlur}/>
                                            </Col>
                                        </Form.Group>
                                    </Form>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button type="button" variant="secondary" onClick={closeModal}>
                                        Abbrechen
                                    </Button>
                                    <Button type="submit" variant="primary" onClick={handleSubmit}>
                                        Aktivieren
                                    </Button>
                                </Modal.Footer>
                            </>
                        )
                    }
                }
            </Formik>
        </Modal>
    );
}

function Coupons()
{
    const context = useConfirmation();
    const { request } = useRequest();
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [modalNotes, setModalNotes] = useState(false);
    const [modalAssign, setModalAssign] = useState(false);
    const [modalCreate, setModalCreate] = useState(false);
    const [modalActivate, setModalActivate] = useState(false);
    const [coupon, setCoupon] = useState(false);

    const [coupons, setCoupons] = useState(false);

    const [mappedCoupons, setMappedCoupons] = useState([]);

    const openNoteModal = (coupon) => {
        setCoupon({ ...coupon });
        setModalNotes(true);
    }

    const openAssignModal = (coupon) => {
        setCoupon(coupon);
        setModalAssign(true);
    }

    const openActivateModal = (coupon) => {
        setCoupon(coupon);
        setModalActivate(true);
    }

    const createCoupon = async (code, maxBookings, discount, note, activated, expired) => {
        let data = { Code: code, MaxBookings: maxBookings, DiscountPercentage: discount, Notes: note, Activated: activated, Expired: expired };
        
        try {
            let result = await request("/Operator/CreateCoupon", data);
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Etwas ist schief gelaufen.");
                }
            }
            else {
                setCoupons((items) => {
                    const newItems = [...items];
                    const obj = {
                        CouponID: json.id,
                        Code: code,
                        MaxBookings: maxBookings,
                        DiscountPercentage: discount,
                        Notes: note,
                        Activated: activated,
                        Expired: expired,
                        UsedCount: 0,
                    };
                    newItems.push(obj);
                    return newItems;
                });
                toast.success("Gutschein wurde erfolgreich erstellt.");
                setModalCreate(false);
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
        
    }

    const deleteCoupon = async (coupon) => {
        try {
            let result = await request("/Operator/DeleteCoupon", { couponID: coupon.CouponID });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Etwas ist schief gelaufen.");
                }
            }
            else {
                setCoupons((items) => {
                    return items.filter((item) => {
                        return item.CouponID != coupon.CouponID;
                    });
                });
                toast.success("Gutschein wurde erfolgreich gelöscht.");
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
    }

    const updateCoupon = async (coupon) => {
        try {
            let result = await request("/Operator/UpdateCoupon", coupon);
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Etwas ist schief gelaufen.");
                }
            }
            else {
                setCoupons((items) => {
                    return items.map((item) => {
                        if (item.CouponID == coupon.CouponID) {
                            return { ...item, ...coupon };
                        }
                        else return item;
                    });
                });
                toast.success("Gutschein wurde erfolgreich bearbeitet.");
                setModalNotes(false);
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
       
    }

    const assignCoupon = async (coupon, organisationID) => {
        let data = { couponID: coupon.CouponID, OrganisationID: organisationID };
        let result = await request("/Operator/AssignCoupon", data);
        let json = result.data;
        if (json.r != 1) {
            if (json.message) {
                toast.error(json.message);
            }
            else {
                toast.error("Etwas ist schief gelaufen.");
            }
        }
        else {
            setCoupons((items) => {
                return items.map((item) => {
                    if (item.CouponID == coupon.CouponID) {
                        item.OrganisationID = organisationID;
                        item.OrganisationInfo = json.organisation;
                        return item;
                    }
                    else return item;
                });
            });
            setModalAssign(false);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                let result = await request("/Operator/GetCoupons");
                let json = result.data;
                if (json.r != 1) {
                    if (json.message) {
                        toast.error(json.message);
                    }
                    else {
                        toast.error("Es ist ein Fehler aufgetreten.");
                    }
                }
                else {
                    setCoupons(json.coupons);
                }
            }
            catch (ex) {
                console.error(ex);
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        };
        fetchData();
    }, []);


    const columns = [
        columnHelper.accessor('StatusString', {
            minSize: 50,            
            header: () => {
                return (
                    <OverlayTrigger popperConfig={{
                        modifiers: {
                            name: "offset",
                            options: {
                                offset: [0, 20]
                            }
                        }
                    }} placement="left" overlay={(
                        <Popover id="business-status-info">
                            <Popover.Header as="h3">Aktiviert</Popover.Header>
                            <Popover.Body>
                                <div><span className="bookedCircle me-2"></span>nein</div>
                                <div><span className="bookedCircle booked me-2"></span>ja</div>
                            </Popover.Body>
                        </Popover>
                    )}>
                        <span>Aktiviert</span>
                    </OverlayTrigger>
                );
            },
            cell: info => {
                let statusCircle = <span className="bookedCircle"></span>;
                if (info.getValue() == "ja") {
                    statusCircle = <span className="bookedCircle booked"></span>;
                }
                return (
                    <OverlayTrigger placement="left" overlay={<Tooltip>{info.getValue()}</Tooltip>}>
                        {statusCircle}
                    </OverlayTrigger>
                );
            },
            enableColumnFilter: true,
            filterFn: customFilterFunction,
            meta: {
                filterComponent: "select",
            }
        }),
        columnHelper.accessor('Code', {
            header: () => <span>Code</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true
        }),
        columnHelper.accessor('MaxBookings', {
            maxSize: 180,
            header: () => <span>Maximale Anwendungen</span>,
            cell: info => info.getValue(),
            /*cell: info => {
                return (info.row.original.OrganisationID == null) ? "" : <a href={"/Operator/Business/" + info.row.original.OrganisationID}>{info.getValue()}</a>;
            },*/
            enableColumnFilter: true
        }),
        columnHelper.accessor('UsedCount', {
            maxSize: 140,
            header: () => <span>Anwendungen</span>,
            cell: info => info.getValue(),
            /*cell: info => {
                return (info.row.original.OrganisationID == null) ? "" : <a href={"/Operator/Business/" + info.row.original.OrganisationID}>{info.getValue()}</a>;
            },*/
            enableColumnFilter: true
        }),
        columnHelper.accessor('DiscountPercentage', {
            maxSize: 140,
            header: () => <span>Rabatt in %</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true
        }),
        columnHelper.accessor('Notes', {
            header: () => <span>Notizen</span>,
            cell: info => info.getValue(),
            enableColumnFilter: true
        }),
        columnHelper.accessor('Activated', {
            header: () => <span>Gültig ab</span>,
            cell: info => info.getValue() ? formatDate(info.getValue()) : "",
            enableColumnFilter: true
        }),
        columnHelper.accessor('Expired', {
            header: () => <span>Gültig bis</span>,
            cell: info => info.getValue() ? formatDate(info.getValue()) : "",
            enableColumnFilter: true
        }),
        columnHelper.accessor('CouponID', {
            header: () => "",
            cell: info => {
                return <div className="text-end">
                    <OverlayTrigger overlay={<Tooltip>Gutschein bearbeiten</Tooltip>}>
                        <Button size="sm" variant="primary" className="me-2" onClick={() => openNoteModal(info.row.original)}><i className="fa-solid fa-fw fa-pen"></i></Button>
                    </OverlayTrigger>
                    <OverlayTrigger overlay={<Tooltip>Organisationen verwalten</Tooltip>}>
                        <Button size="sm" variant="primary" className="me-2" href={"/Operator/Coupons/" + info.getValue() }><i className="fa-solid fa-fw fa-list"></i></Button>
                    </OverlayTrigger>
                    <OverlayTrigger overlay={<Tooltip>Gutschein löschen</Tooltip>}>
                        <Button size="sm" variant="danger" onClick={() => context.openModal("Wirklich löschen?", "Einmal gelöscht, kann dieser Gutschein nicht wiederhergestellt werden.", () => deleteCoupon(info.row.original))}><i className="fa-solid fa-fw fa-trash"></i></Button>
                    </OverlayTrigger>
                </div>;
            },
            enableColumnFilter: false,
            enableSorting: false
        })
    ];

    const reactTable = useReactTable({
        data: coupons || [],
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    if (coupons === false) return <Loader />

    return (
        <>
            <Button onClick={() => setModalCreate(true)}>Neuer Gutschein</Button>
        <Table striped>
            <thead>
                {reactTable.getHeaderGroups().map(headerGroup => (
                    <TableHeader key={headerGroup.id} headerGroup={headerGroup} flexRender={flexRender} table={reactTable} />
                ))}
            </thead>
            <tbody>
                {reactTable.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map(cell => {
                            return (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            )
                        })}
                    </tr>
                ))}
            </tbody>
            </Table>
            <ModalEditCoupon modal={modalNotes} setModal={setModalNotes} coupon={coupon} setCoupon={setCoupon} updateNote={updateCoupon} />
            <ModalCreateCoupon modal={modalCreate} setModal={setModalCreate} createCoupon={createCoupon} />
        </>
    );
}

export default Coupons;